/**
 * This file implements a page-wide 12-column Grid as the designers use it in Zeplin.
 *
 * Multiple Grid instances can be freely nested. When doing so, the nested Grids
 * should be created with the correct (lower) column count.
 *
 * The topmost `Grid` will extend to full screen width and has to be wrapped in some
 * other layout component (e.g. Section) to enforce a maximum width on large screens.
 *
 * The gutter is optional and its measurements depend on the current viewport.
 *
 * Grid children must we wrapped in a `GridArea` to define their poisition and
 * extent within the surrounding Grid.
 *
 * Unlike other Grid implementations, this one assumes that, on any nesting depth,
 * we will always refer to the one, globally defined, 12-column base layout, and
 * that children can "respond" to their position inside this topmost `Grid`. This
 * may seem a bit unfamiliar at first, but this is actually how we've been solving
 * "viewport responsiveness" all the time. With the `useGridContext` hook, a
 * component can now determine the size of its nearest ascendant Grid cell just
 * in the same way as its viewport, e.g.:
 *
 * - `useIsViewportMobile()` -> in mobile, use a different font-size
 * - `useGridContext()` -> if spanning more than 3 columns, wrap content vertically
 */
import styled, { css } from 'styled-components';
import { media } from '../themes/theme';
export { GridArea } from './components/GridArea';
export { useGridContext } from './hooks/useGridContext';
export const Grid = styled.div `
  display: grid;
  grid-template-columns: repeat(${p => p.columns || 12}, minmax(0, 1fr));

  ${p => p.hasGapH &&
    css `
      column-gap: 1rem;

      ${media.tablet `
        column-gap: 2rem;
      `}
    `}

  ${p => p.hasGapV &&
    css `
      row-gap: 1rem;

      ${media.tablet `
        row-gap: 2rem;
      `}
    `}
`;
